import i18n from '@/i18n';
import { FetchData } from '@/main';

const FEATUREBASE_SDK_ID = 'featurebase-sdk';
const FEATUREBASE_SDK_URL = 'https://do.featurebase.app/js/sdk.js';
const DEFAULT_WIDGET_PLACEMENT: FeaturebaseWidgetPlacement = 'bottom-right';
const DEFAULT_WIDGET_THEME: FeaturebaseWidgetTheme = 'light';
const LOG_PREFIX = '[Featurebase]';

export type FeaturebaseWidgetPlacement = 'bottom-left' | 'bottom-right';
export type FeaturebaseWidgetTheme = 'dark' | 'light';
type FeaturebaseCommand = 'identify' | 'initialize_survey_widget';
type FeaturebaseCallback = (error?: unknown) => void;

export interface FeaturebaseUserIdentity {
  email?: string;
  name?: string;
  userId?: string;
}

export interface FeaturebaseConfig {
  locale?: string;
  organization: string;
  placement?: FeaturebaseWidgetPlacement;
  theme?: FeaturebaseWidgetTheme;
}

interface FeaturebaseIdentifyPayload extends FeaturebaseUserIdentity {
  featurebaseJwt: string;
  organization: string;
}

interface FeaturebaseSurveyPayload {
  email?: string;
  featurebaseJwt?: string;
  locale?: string;
  organization: string;
  placement: FeaturebaseWidgetPlacement;
  theme: FeaturebaseWidgetTheme;
}

type FeaturebaseCommandPayload = FeaturebaseIdentifyPayload | FeaturebaseSurveyPayload;
type FeaturebaseCommandArgs = [FeaturebaseCommand, FeaturebaseCommandPayload, FeaturebaseCallback?];
type FeaturebaseQueueFunction = ((
  command: FeaturebaseCommand,
  payload: FeaturebaseCommandPayload,
  callback?: FeaturebaseCallback,
) => void) & { q?: FeaturebaseCommandArgs[] };

interface FeaturebaseTokenResponse {
  Email?: string;
  featurebaseJwt?: string;
  Name?: string;
  Token?: string;
  UserId?: string;
  email?: string;
  name?: string;
  token?: string;
  userId?: string;
}

class FeaturebaseService {
  private initializePromise: Promise<void> | null = null;
  private lastIdentifyKey = '';
  private lastInitializationKey = '';
  private missingOrganizationLogged = false;
  private sdkLoadPromise: Promise<void> | null = null;
  private surveyWidgetInitialized = false;

  public async initializeForUser(
    options: (FeaturebaseUserIdentity & FeaturebaseConfig) | null | undefined,
  ): Promise<void> {
    const normalizedUser = normalizeUserIdentity(options);

    if (!normalizedUser) {
      return;
    }

    const config = normalizeFeaturebaseConfig(options);

    if (!config.organization) {
      if (!this.missingOrganizationLogged) {
        this.logWarning('Skipping initialization because Featurebase organization is missing.');
        this.missingOrganizationLogged = true;
      }

      return;
    }

    const initializationKey = JSON.stringify({
      config,
      user: normalizedUser,
    });

    if (this.lastInitializationKey === initializationKey) {
      return;
    }

    if (this.initializePromise) {
      await this.initializePromise;

      if (this.lastInitializationKey === initializationKey) {
        return;
      }
    }

    this.initializePromise = this.runInitialization(normalizedUser, config, initializationKey)
      .catch((error: unknown) => {
        this.logWarning('Unable to initialize the survey widget.', error);
      })
      .finally(() => {
        this.initializePromise = null;
      });

    await this.initializePromise;
  }

  private async loadSdk(): Promise<void> {
    if (isFeaturebaseSdkReady()) {
      return;
    }

    if (!this.sdkLoadPromise) {
      this.sdkLoadPromise = new Promise<void>((resolve, reject) => {
        ensureFeaturebaseQueue();

        const existingScript = document.getElementById(FEATUREBASE_SDK_ID) as HTMLScriptElement | null;
        const resolveScript = () => {
          if (isFeaturebaseSdkReady()) {
            resolve();
            return;
          }

          reject(new Error('Featurebase SDK did not become ready after loading.'));
        };
        const rejectScript = () => {
          reject(new Error('Failed to load the Featurebase SDK.'));
        };

        if (existingScript) {
          if (existingScript.dataset.loaded === 'true') {
            resolveScript();
            return;
          }

          existingScript.addEventListener('load', resolveScript, { once: true });
          existingScript.addEventListener('error', rejectScript, { once: true });
          return;
        }

        const script = document.createElement('script');
        script.id = FEATUREBASE_SDK_ID;
        script.async = true;
        script.src = FEATUREBASE_SDK_URL;
        script.addEventListener('load', () => {
          script.dataset.loaded = 'true';
          resolveScript();
        }, { once: true });
        script.addEventListener('error', rejectScript, { once: true });
        document.head.appendChild(script);
      }).catch((error: unknown) => {
        this.sdkLoadPromise = null;
        throw error;
      });
    }

    await this.sdkLoadPromise;
  }

  private logWarning(message: string, error?: unknown): void {
    if (error) {
      // eslint-disable-next-line no-console
      console.warn(LOG_PREFIX, message, error);
      return;
    }

    // eslint-disable-next-line no-console
    console.warn(LOG_PREFIX, message);
  }

  private async requestSignedIdentity(
    user: FeaturebaseUserIdentity,
    organization: string,
  ): Promise<FeaturebaseIdentifyPayload | null> {
    const response = await FetchData('/Application/GetFeaturebaseToken', '') as FeaturebaseTokenResponse | undefined;
    const featurebaseJwt = normalizeText(response?.featurebaseJwt ?? response?.token ?? response?.Token);

    if (!featurebaseJwt) {
      this.logWarning('Skipping initialization because the backend token response did not contain `token`.');
      return null;
    }

    const mergedIdentity = normalizeUserIdentity({
      email: response?.email ?? response?.Email ?? user.email,
      name: response?.name ?? response?.Name ?? user.name,
      userId: response?.userId ?? response?.UserId ?? user.userId,
    });

    if (!mergedIdentity) {
      this.logWarning('Skipping initialization because no authenticated user identity was available.');
      return null;
    }

    return {
      ...mergedIdentity,
      featurebaseJwt,
      organization,
    };
  }

  private async runFeaturebaseCommand(
    command: FeaturebaseCommand,
    payload: FeaturebaseCommandPayload,
  ): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const featurebase = window.Featurebase;

      if (typeof featurebase !== 'function') {
        reject(new Error('Featurebase SDK is unavailable.'));
        return;
      }

      const invokeFeaturebase = featurebase as (...args: FeaturebaseCommandArgs) => void;

      invokeFeaturebase(command, payload, (error?: unknown) => {
        if (error) {
          reject(error);
          return;
        }

        resolve();
      });
    });
  }

  private async runInitialization(
    user: FeaturebaseUserIdentity,
    config: FeaturebaseConfig,
    initializationKey: string,
  ): Promise<void> {
    await this.loadSdk();

    const identifyPayload = await this.requestSignedIdentity(user, config.organization);

    if (!identifyPayload) {
      return;
    }

    const identifyKey = JSON.stringify(identifyPayload);

    if (this.lastIdentifyKey !== identifyKey) {
      await this.runFeaturebaseCommand('identify', identifyPayload);
      this.lastIdentifyKey = identifyKey;
    }

    if (!this.surveyWidgetInitialized) {
      await this.runFeaturebaseCommand('initialize_survey_widget', {
        email: identifyPayload.email,
        featurebaseJwt: identifyPayload.featurebaseJwt,
        locale: config.locale,
        organization: config.organization,
        placement: config.placement ?? DEFAULT_WIDGET_PLACEMENT,
        theme: config.theme ?? DEFAULT_WIDGET_THEME,
      });
      this.surveyWidgetInitialized = true;
    }

    this.lastInitializationKey = initializationKey;
  }
}

function ensureFeaturebaseQueue(): void {
  if (typeof window.Featurebase === 'function' && !Array.isArray(window.Featurebase.q)) {
    return;
  }

  if (typeof window.Featurebase !== 'function') {
    const featurebaseQueue: FeaturebaseQueueFunction = function(...args: FeaturebaseCommandArgs): void {
      featurebaseQueue.q = featurebaseQueue.q || [];
      featurebaseQueue.q.push(args);
    };

    featurebaseQueue.q = featurebaseQueue.q || [];
    window.Featurebase = featurebaseQueue as unknown as typeof window.Featurebase;
  }
}

function getCurrentLocale(): string | undefined {
  const locale = i18n.global.locale as unknown;

  if (typeof locale === 'string') {
    return normalizeText(locale);
  }

  if (locale && typeof locale === 'object' && 'value' in locale) {
    return normalizeText((locale as { value: string }).value);
  }

  return undefined;
}

function normalizeFeaturebaseConfig(
  config: FeaturebaseConfig | null | undefined,
): FeaturebaseConfig {
  return {
    locale: normalizeText(config?.locale) ?? getCurrentLocale(),
    organization: normalizeText(config?.organization) ?? '',
    placement: config?.placement ?? DEFAULT_WIDGET_PLACEMENT,
    theme: config?.theme ?? DEFAULT_WIDGET_THEME,
  };
}

function isFeaturebaseSdkReady(): boolean {
  if (typeof window.Featurebase !== 'function') {
    return false;
  }

  const existingScript = document.getElementById(FEATUREBASE_SDK_ID) as HTMLScriptElement | null;

  if (existingScript?.dataset.loaded === 'true') {
    return true;
  }

  return !Array.isArray(window.Featurebase.q);
}

function normalizeText(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();

  return trimmedValue ? trimmedValue : undefined;
}

function normalizeUserIdentity(
  user: FeaturebaseUserIdentity | null | undefined,
): FeaturebaseUserIdentity | null {
  if (!user) {
    return null;
  }

  const email = normalizeText(user.email);
  const userId = normalizeText(user.userId);
  const name = normalizeText(user.name) ?? email ?? userId;

  if (!email && !userId) {
    return null;
  }

  return {
    email,
    name,
    userId,
  };
}

export const featurebaseService = new FeaturebaseService();
